import FullPageLoader from '@components/core/full-page-loader/full-page-loader';
import { Check, WarningOutlined } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { CssBaseline, ThemeProvider } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { queryClient } from '@src/lib/react-query';
import { LoaderProvider } from '@src/providers/loader/loader-provider';
import { useThemeStore } from '@src/stores/theme-store';
import { BrandPaletteKeys, BrandPaletteKeysEnum } from '@src/themes';
import { QueryClientProvider } from '@tanstack/react-query';
import { getCookie, ToastUtility } from '@utils';
import { PropsWithChildren, Suspense, useEffect } from 'react';
import { CloseButtonProps, ToastContainer } from 'react-toastify';

export const AppProvider = ({ children }: PropsWithChildren) => {
  const partner = getCookie('partner');
  const { setTheme } = useThemeStore.use.actions();
  const theme = useThemeStore.use.theme();

  useEffect(() => {
    setTheme(
      (partner as BrandPaletteKeys) ?? BrandPaletteKeysEnum.MaxMyInterest,
    );
  }, []);
  ToastUtility.init(theme); // customize toasts based on theme

  return (
    <Suspense fallback={<FullPageLoader />}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            <ToastContainer
              stacked
              position='bottom-center'
              style={{
                zIndex: theme.zIndex.drawer - 1,
              }}
              closeButton={({ closeToast }: CloseButtonProps) => (
                <IconButton
                  onClick={closeToast}
                  sx={{
                    marginLeft: 'auto',
                    alignSelf: 'flex-start',
                  }}
                >
                  <Close />
                </IconButton>
              )}
              icon={({ type }) => {
                switch (type) {
                  case 'info':
                    return <InfoOutlined />;
                  case 'error':
                    return <ErrorOutline />;
                  case 'success':
                    return <Check />;
                  case 'warning':
                    return <WarningOutlined />;
                  default:
                    return null;
                }
              }}
            />
            <LoaderProvider>{children}</LoaderProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </Suspense>
  );
};
